//@ts-check
import { logger } from "../../logger/Logger";
import { LmvBox3 as Box3 } from '../../wgs/scene/LmvBox3';
import { MeshFlags } from '../../wgs/scene/MeshFlags';
import THREE from "three";
import { calculateLevelsToProcess, calculateStartLevel } from './RcsLodUtils.js';
import { FEATURE_SIZE_SCALING_FACTOR, INSTANCE_TREE_MODE, LOD_MAPPING_MODE, DIRECT_MODE_LEVELS_IN_LOD0, MAX_LOD_LEVELS } from './RcsConstants.js';
import { getLodRange, getRequiredLodLevels } from './RcsLodMapping.js';
import { NaryBVH, NaryBvhNodeArray } from '../../wgs/scene/NaryBVH';
import { BVHSortFuncType } from '../../wgs/scene/BVHBuilder';
import { USE_WEBGPU } from '../../wgs/globals';

/** @import { LodRange } from './RcsLodMapping.js' */
/** @import { RcsPropDbLoader} from'./RcsPropDbLoader' */
/** @import { InstanceTree} from '../../wgs/scene/InstanceTree'} */
/** @import { RcsLoadOptions, OctreeNode, OctreeLeafNode, RcsOctreeStructure } from './RcsFileLoader'} */
/** @import { RcsHeader, RcsChunk, UmbrellaLeafNode } from './RcsExtractorCore' */

// ── Type Definitions ─────────────────────────────────────────────────


/**
 * Describes how a range of points from an umbrella leaf should be written
 * into a parent inner node's geometry buffer during hierarchical LOD loading.
 * @typedef {Object} ParentLodRange
 * @property {number} bvhNodeId  - BVH node ID of the ancestor that receives the points
 * @property {number} level      - SVO octree level the points originate from
 * @property {number} rangeStart - Start index in the ancestor's vertex buffer
 * @property {number} rangeEnd   - End index (exclusive) in the ancestor's vertex buffer
 */

/**
 * Fragment data arrays satisfying the Model / FragmentList contract.
 * @typedef {Object} RcsFragments
 * @property {number}       length          - Total number of fragments
 * @property {Int32Array}   fragId2dbId     - Maps fragment ID → property-database node ID
 * @property {number[][]}   dbId2fragId     - Reverse map: property-database node ID → fragment IDs (one dbId may map to many frags; kept as a jagged array)
 * @property {Float32Array} transforms      - 3×4 affine transform matrices (12 floats per fragment)
 * @property {Float32Array} boxes           - Axis-aligned bounding boxes (6 floats per fragment: minX, minY, minZ, maxX, maxY, maxZ)
 * @property {Uint16Array}  visibilityFlags - Per-fragment visibility / loading bit-flags (see MeshFlags)
 * @property {number}       numLoaded       - Count of fragments with geometry currently loaded
 * @property {boolean}      initialized     - True once the arrays are populated
 */

/**
 * Base shape of the model-level metadata. The full {@link RcsMetadata} type adds
 * a `"world up vector"` key (with spaces) which cannot be expressed using the
 * `@property` JSDoc syntax — it is contributed via an intersection type below.
 * @typedef {Object} RcsMetadataBase
 * @property {string} version  - RCS file version ("major.minor")
 * @property {{ num_fragments: number,
 *              num_materials: number,
 *              num_geoms: number,
 *              num_umbrella_nodes: number }} stats - Aggregate statistics for the model
 */

/**
 * Model-level metadata exposed via `model.getData().metadata`.
 * @typedef {RcsMetadataBase & { "world up vector": { XYZ: number[] } }} RcsMetadata
 */

/**
 * RCS model data package — analogous to OtgPackage for OTG files.
 *
 * Holds the model data contract required by `Model` / `FragmentList`
 * (metadata, bbox, fragments, globalOffset) together with the per-node
 * BVH metadata needed by the OOC tile manager.
 *
 * ## Data model
 *
 * An RCS file stores a single point cloud scan as a Sparse Voxel Octree (SVO).
 * The file's top-level "umbrella tree" partitions the scan into spatial regions
 * (umbrella leaf nodes), each containing an independent SVO with per-level
 * point counts.
 *
 * ### BVH tree structure
 *
 * The package builds a BVH that mirrors this two-level structure:
 *
 * 1. **Octree interior nodes** — Correspond 1:1 to the source umbrella octree.
 *    Each interior BVH node may accumulate coarse-LOD points that are "pulled
 *    up" from its descendant leaves (see Hierarchical LOD below).
 *
 * 2. **Umbrella leaf nodes (LOD 0)** — Each octree leaf maps to one umbrella
 *    leaf in the file. The LOD-0 BVH node owns the coarsest-resolution fragment.
 *
 * 3. **LOD chain nodes (LOD 1, 2, …)** — Appended as a linear child chain
 *    below each umbrella leaf. Each holds one fragment that covers a finer
 *    LOD range of the same SVO data. The OOC tile manager loads the chain
 *    top-down (coarsest first) for progressive refinement.
 *
 * ### Hierarchical LOD ("point pull-up")
 *
 * For LOD-0 leaves, the lowest-resolution octree levels are distributed to
 * ancestor interior nodes instead of staying in the leaf. This gives each
 * interior node a coarse point-cloud representation of its entire subtree,
 * enabling a visible approximation before any leaf data has been fetched.
 * The allocation is volume-weighted: each ancestor receives roughly
 * `pointsPerLevel × (leafVolume / ancestorVolume)` points.
 *
 * ### Per-node metadata (all indexed by BVH node ID)
 *
 * | Array                   | Type          | Description                                     |
 * |-------------------------|---------------|-------------------------------------------------|
 * | `_nodeLodLevel`         | `Int8Array`   | LOD level (0+ for umbrella/LOD chain, −1 otherwise) |
 * | `_nodeUmbrellaIndex`    | `Int32Array`  | Umbrella leaf index (−1 for non-leaf nodes)     |
 * | `_nodePointCount`       | `Int32Array`  | Allocated HLOD point count for inner nodes      |
 * | `_nodeOctreeLevel`      | `Int16Array`  | Depth in the source octree                      |
 * | `_nodeFeatureSize`      | `Float32Array`| Feature size for LOD scheduling (−1 = unset)    |
 * | `_nodeMaxFeatureSize`   | `Float32Array`| Max feature size in subtree                     |
 * | `_nodeParentLodRanges`  | `Array`       | Sparse; per-leaf HLOD write ranges              |
 */
export class RcsPackage {

  /**
   * Build the BVH and all per-node metadata from the parsed RCS octree.
   *
   * Performs a single recursive traversal of the octree, writing directly
   * into the NaryBvhNodeArray and flat typed arrays — no intermediate JS
   * objects are created.
   *
   * @param {RcsHeader} header - Parsed RCS file header (version, scan bounds, etc.)
   * @param {RcsChunk[]} chunks - File chunk directory entries
   * @param {UmbrellaLeafNode[]} umbrellaLeafNodes - Per-umbrella-leaf metadata (SVO bounds, point counts, etc.)
   * @param {RcsOctreeStructure} octreeStructure - Parsed umbrella octree with bounding boxes and child pointers
   * @param {Map<number, number>} fragmentIdMap - Maps `umbrellaIndex * MAX_LOD_LEVELS + lodLevel` to fragment ID
   * @param {RcsLoadOptions} options - Load options forwarded to model / BVH construction
   */
  constructor(header, chunks, umbrellaLeafNodes, octreeStructure, fragmentIdMap, options) {

    /** @type {boolean} - Discriminator flag to identify RCS data packages */
    this.isRCS = true;

    /** @type {boolean} - True once geometry loading is complete (mirrors OtgPackage.loadDone) */
    this.loadDone = false;

    // ── RCS file data (read-only after construction) ─────────────
    /** @type {RcsHeader} - Parsed RCS file header */
    this.header = header;
    /** @type {RcsChunk[]} - File chunk directory entries */
    this.chunks = chunks;
    /** @type {UmbrellaLeafNode[]} - Per-umbrella-leaf SVO metadata */
    this.umbrellaLeafNodes = umbrellaLeafNodes;

    /**
     * Prefix sum of umbrella-leaf SVO payload sizes: element `i` holds the byte offset
     * of leaf `i`'s payload relative to the first leaf (i.e. the sum of `fileSize` for
     * all `j < i`). Precomputed so callers can resolve a leaf's data location in O(1).
     * @type {Float64Array}
     */
    this._umbrellaLeafDataOffsets = new Float64Array(umbrellaLeafNodes?.length ?? 0);
    // Note that _umbrellaLeafDataOffsets[0] is zero initialized and thus not written.
    for (let i = 1; i < this._umbrellaLeafDataOffsets.length; i++) {
      this._umbrellaLeafDataOffsets[i] = this._umbrellaLeafDataOffsets[i - 1] + umbrellaLeafNodes[i - 1].fileSize;
    }

    // ── PropDB contract fields (set later by RcsPropDbLoader) ────
    /** @type {RcsPropDbLoader|null} - Property database loader instance */
    this.propDbLoader = null;
    /** @type {InstanceTree|null} - Property database instance tree */
    this.instanceTree = null;
    /** @type {boolean} - True once the property database has been built */
    this.hasObjectProperties = false;

    // ── Per-node metadata arrays (populated by _build) ─────────
    /** @type {Int8Array} - LOD level per node (−1 = non-umbrella / structural) */
    // @ts-expect-error assigned in _build
    this._nodeLodLevel = null;
    /** @type {Int32Array} - Umbrella leaf index per node (−1 = not an umbrella leaf) */
    // @ts-expect-error assigned in _build
    this._nodeUmbrellaIndex = null;
    /** @type {Int32Array} - HLOD point count accumulated for inner nodes */
    // @ts-expect-error assigned in _build
    this._nodePointCount = null;
    /** @type {Int16Array} - Octree depth per node (0 = root level) */
    // @ts-expect-error assigned in _build
    this._nodeOctreeLevel = null;
    /** @type {Float32Array} - Feature size per node (−1 = unset sentinel) */
    // @ts-expect-error assigned in _build
    this._nodeFeatureSize = null;
    /** @type {Float32Array} - Max feature size in subtree */
    // @ts-expect-error assigned in _build
    this._nodeMaxFeatureSize = null;
    /** @type {Array<ParentLodRange[]|undefined>} - Sparse HLOD write ranges (only set for umbrella leaves) */
    // @ts-expect-error assigned in _build
    this._nodeParentLodRanges = null;

    // ── Model / FragmentList contract fields (populated by _build) ──
    /** @type {RcsMetadata} */
    // @ts-expect-error assigned in _build
    this.metadata = null;
    /** @type {Box3} */
    // @ts-expect-error assigned in _build
    this.bbox = null;
    /** @type {RcsFragments} */
    // @ts-expect-error assigned in _build
    this.fragments = null;
    /** @type {RcsLoadOptions} */
    // @ts-expect-error assigned in _build
    this.loadOptions = null;
    /** @type {THREE.Vector3} */
    // @ts-expect-error assigned in _build
    this.globalOffset = null;

    // ── Build the BVH and populate all metadata ──────────────────
    const { bvh, numFragments, numCoarseNodes } = this._build(octreeStructure, fragmentIdMap, options);

    /** @type {NaryBVH} - The N-ary bounding volume hierarchy for OOC traversal */
    // @ts-expect-error null for empty models
    this.bvh = bvh;

    /**
     * Number of "coarse" nodes (interior nodes with points + LOD-0 umbrella leaves).
     * The set of these nodes is a low resolution representation of the entire point cloud.
     * Used by the RCS loader to drive load-progress reporting.
     * @type {number}
     */
    this.numCoarseNodes = numCoarseNodes;

    logger.info(`N-ary BVH built: ${bvh ? bvh.nodes.nodeCount : 0} nodes, ${numFragments} fragments`);
  }

  // ── NodeId-centric accessors ─────────────────────────────────────

  /**
   * LOD level for the given BVH node.
   * Returns 0+ for umbrella leaf / LOD-chain nodes, −1 for inner or structural nodes.
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodeLodLevel(nodeId) {return this._nodeLodLevel[nodeId];}

  /**
   * Umbrella leaf index for the given BVH node.
   * Returns −1 for inner (non-umbrella-leaf) nodes.
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodeUmbrellaIndex(nodeId) {return this._nodeUmbrellaIndex[nodeId];}

  /**
   * Allocated HLOD point count for inner nodes, or the leaf's own point count
   * when the LOD-0 umbrella leaf had points pulled up.
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodePointCount(nodeId) {return this._nodePointCount[nodeId];}

  /**
   * Depth of this node in the source umbrella octree (0 = root).
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodeOctreeLevel(nodeId) {return this._nodeOctreeLevel[nodeId];}

  /**
   * Characteristic feature size for LOD scheduling.
   * Derived from the SVO voxel size at the node's start level.
   * Returns −1 if no feature size has been assigned.
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodeFeatureSize(nodeId) {return this._nodeFeatureSize[nodeId];}

  /**
   * Maximum feature size in the subtree rooted at this node.
   * Used together with `getNodeFeatureSize` to compute LOD priority ratios.
   * @param {number} nodeId - BVH node index
   * @returns {number}
   */
  getNodeMaxFeatureSize(nodeId) {return this._nodeMaxFeatureSize[nodeId];}

  /**
   * HLOD parent write ranges for an umbrella leaf.
   * Each entry describes where that leaf's decoded points should be written
   * into an ancestor inner node's vertex buffer.
   * Returns `undefined` for non-leaf nodes or leaves without HLOD distribution.
   * @param {number} nodeId - BVH node index
   * @returns {ParentLodRange[]|undefined}
   */
  getNodeParentLodRanges(nodeId) {return this._nodeParentLodRanges[nodeId];}

  /**
   * True if the node is not an umbrella leaf, i.e. an interior octree node or a
   * structural BVH node (such as the transparent root). Defined as
   * `umbrellaIndex === -1`; it does not imply the node carries HLOD geometry
   * (use {@link isInnerNodeWithPoints} for that).
   *
   * @param {number} nodeId - BVH node index
   * @returns {boolean}
   */
  isInnerNode(nodeId) {
    return this._nodeUmbrellaIndex[nodeId] === -1;
  }

  /**
   * True if the node is an interior octree node that has been assigned
   * HLOD geometry (i.e. it received "pulled-up" points from descendant leaves).
   *
   * Interior nodes with zero point count exist as structural BVH nodes but have
   * no geometry — they are not considered inner nodes by this predicate.
   * @param {number} nodeId - BVH node index
   * @returns {boolean}
   */
  isInnerNodeWithPoints(nodeId) {
    return this._nodeUmbrellaIndex[nodeId] === -1 && this._nodePointCount[nodeId] > 0;
  }

  /**
   * Inner nodes that carry points and LOD-0 umbrella leaves make up a
   * low-resolution representation of the point cloud.
   *
   * At time of writing coarse nodes are loaded once and never unloaded. The ratio
   * of loaded coarse nodes defines load progress as shown by the indicator.
   * @param {number} nodeId - BVH node index
   * @returns {boolean}
   */
  isCoarseNode(nodeId) {
    return this.isInnerNodeWithPoints(nodeId) || this._nodeLodLevel[nodeId] === 0;
  }

  /**
   * Get the umbrella leaf node file-level metadata by umbrella index.
   * @param {number} umbrellaIndex - Index into the umbrella leaf array
   * @returns {UmbrellaLeafNode}
   */
  getUmbrellaLeafNode(umbrellaIndex) {
    return this.umbrellaLeafNodes[umbrellaIndex];
  }

  /**
   * Byte offset of an umbrella leaf's SVO payload relative to the first leaf, i.e. the
   * sum of all preceding leaves' `fileSize`. Add the voxel chunk's file start offset to
   * get an absolute offset.
   * @param {number} umbrellaIndex - Index into the umbrella leaf array
   * @returns {number}
   */
  getUmbrellaLeafDataOffset(umbrellaIndex) {
    return this._umbrellaLeafDataOffsets[umbrellaIndex];
  }

  // ── Derived queries ──────────────────────────────────────────────

  /**
   * Compute the effective screen-space error for a BVH node during LOD loading.
   *
   * Interior nodes with HLOD geometry  use a large constant boost so they
   *  stay high in the load priority and remain resident.
   *
   * LOD-0 nodes get boosted priority; These nodes are given the priority
   * of the highest parent node into which points can be pulled up.
   * The motivation for this is, that we have to load the LOD0 to perform the
   * pull up into the parent nodes and thus the LOD0 nodes inherit the priority
   * of the highest parent node with into which points can be pulled up.
   *
   * @param {number} nodeId - BVH node index
   * @param {number} screenSpaceError - Raw screen-space error from the OOC manager
   * @returns {number} Adjusted screen-space error for priority sorting
   */
  getLoadingScreenSpaceError(nodeId, screenSpaceError) {
    if (this.isCoarseNode(nodeId)) {
      return 1e5 + screenSpaceError;
    }
    const maxFS = this._nodeMaxFeatureSize[nodeId];
    if (maxFS === 0) return screenSpaceError;
    const nodeFS = this._nodeFeatureSize[nodeId];
    // Scale error by sqrt(maxFeatureSize / nodeFeatureSize): coarser (larger) features in
    // the subtree increase effective error for this node’s finer LOD slot.
    // Note: feature sizes are stored squared, so sqrt(maxFS / nodeFS) yields the linear ratio.
    return Math.max(1, Math.sqrt(maxFS / nodeFS)) * screenSpaceError;
  }

  /**
   * Compute the memory required to load the fragment owned by a BVH node.
   * Accounts for the interleaved vertex layout (position + color) and, on WebGL,
   * the identity index buffer required for point rendering.
   * @param {number} nodeId - BVH node index
   * @returns {number} Estimated byte size
   */
  computeNodeMemorySize(nodeId) {
    // Per point: 24 bytes of interleaved vertex data (xyz + rgb, 6 × 4-byte floats).
    // WebGPU stores point data in an SSBO without index buffers (see PointDataBuffer).
    // WebGL also allocates an identity index buffer: for < 2^16 points a Uint16 index
    // (2 bytes/point) is used, rounded up to an even length (+2 bytes when odd);
    // otherwise a Uint32 index (4 bytes/point) is used.
    /** @param {number} pc - point count */
    const sizeFor = (pc) => {
      const vertexBytes = pc * 24;
      if (USE_WEBGPU) {
        return vertexBytes;
      }
      return pc < 65536 ? vertexBytes + pc * 2 + pc % 2 * 2 : vertexBytes + pc * 4;
    };

    const ui = this._nodeUmbrellaIndex[nodeId];
    // Inner octree node (HLOD geometry or structural BVH node): point count is
    // stored directly on the node during the build pass.
    if (ui === -1) return sizeFor(this._nodePointCount[nodeId]);

    const lod = this._nodeLodLevel[nodeId];
    // LOD-0 umbrella leaf: coarse fragment for this umbrella; point count is
    // also stored directly on the node.
    if (lod === 0) {
      return sizeFor(this._nodePointCount[nodeId]);
    }

    // LOD 1+ chain node: finer-detail fragment whose point count is not stored
    // on the node — derive it from the umbrella leaf's SVO distribution over
    // this LOD level's level/point range.
    const leafNode = this.umbrellaLeafNodes[ui];
    /** @type {LodRange} */
    const range = getLodRange(lod);
    const start = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.startLevel : range.startPoint;
    const end = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.endLevel : range.endPoint;
    return sizeFor(calculateLevelsToProcess(leafNode.numSvoLeafNodes, start, end).totalPoints);
  }

  // ── Build pipeline (private) ─────────────────────────────────────

  /**
   * Core build routine: allocates typed arrays, traverses the octree once,
   * populates the NaryBvhNodeArray, assigns inner-node fragments, and
   * initializes fragment data.
   *
   * @private
   * @param {RcsOctreeStructure} octreeStructure - Parsed octree
   * @param {Map<number, number>} fragmentIdMap - packed (umbrellaIndex, lod) → fragId
   * @param {RcsLoadOptions} options - Load / BVH options
   * @returns {{ bvh: NaryBVH|null, numFragments: number, numCoarseNodes: number }}
   */
  _build(octreeStructure, fragmentIdMap, options) {
    const header = this.header;
    const umbrellaLeafNodes = this.umbrellaLeafNodes;

    const bbox = new Box3(
      new THREE.Vector3(header.scanBounds[0], header.scanBounds[1], header.scanBounds[2]),
      new THREE.Vector3(header.scanBounds[3], header.scanBounds[4], header.scanBounds[5])
    );
    // Offset applied to all model coordinates (world → viewer space). Taken from
    // the load options for multi-model aggregation; defaults to zero otherwise.
    this.globalOffset = new THREE.Vector3(
      options.globalOffset?.x ?? 0,
      options.globalOffset?.y ?? 0,
      options.globalOffset?.z ?? 0
    );
    bbox.min.sub(this.globalOffset);
    bbox.max.sub(this.globalOffset);

    if (!umbrellaLeafNodes || umbrellaLeafNodes.length === 0) {
      logger.warn("No umbrella nodes available for BVH construction");
      this._initEmptyModel(bbox, options);
      return { bvh: null, numFragments: 0, numCoarseNodes: 0 };
    }

    this._annotateOctreeWithLeafVolumes(octreeStructure);

    // ── Count nodes ──────────────────────────────────────────────
    // The octree root is merged into the opaque BVH root, so
    // base nodes = max(2, octreeNodes + 1)  (2 BVH roots, octree root reused).
    const numUmbrellaLeaves = umbrellaLeafNodes.length;
    const baseNodeCount = Math.max(2, octreeStructure.nodes.length + 1);

    let additionalLodNodes = 0;
    for (let i = 0; i < numUmbrellaLeaves; i++) {
      const requiredLods = getRequiredLodLevels(umbrellaLeafNodes[i].numSvoLeafNodes);
      additionalLodNodes += Math.max(0, requiredLods.length - 1);
    }

    const totalNodeCount = baseNodeCount + additionalLodNodes;
    const nodeArray = new NaryBvhNodeArray(totalNodeCount);

    // ── Allocate flat per-node arrays ────────────────────────────

    this._nodeLodLevel = new Int8Array(totalNodeCount).fill(-1);
    this._nodeUmbrellaIndex = new Int32Array(totalNodeCount).fill(-1);
    this._nodePointCount = new Int32Array(totalNodeCount);
    this._nodeOctreeLevel = new Int16Array(totalNodeCount);
    this._nodeFeatureSize = new Float32Array(totalNodeCount).fill(-1);
    this._nodeMaxFeatureSize = new Float32Array(totalNodeCount);
    this._nodeParentLodRanges = new Array(totalNodeCount);

    // ── Counters ─────────────────────────────────────────────────
    let nextNodeId = 0;
    let nextLodNodeIndex = baseNodeCount;
    let innerNodeFragmentIndex = fragmentIdMap.size;

    /** @type {Array<{fragId: number, bvhNodeId: number}>} */
    const innerNodeEntries = [];

    const octreeNodesById = new Map();
    for (const node of octreeStructure.nodes) {
      octreeNodesById.set(node.id, node);
    }

    // ── BVH roots ────────────────────────────────────────────────
    const opaqueRootId = nextNodeId++; // node 0
    const transparentRootId = nextNodeId++; // node 1

    nodeArray.setBoxThree(transparentRootId, bbox);
    nodeArray.setFlags(transparentRootId, 0); // empty leaf, never pruned

    // ── Ancestor stacks for HLOD distribution ────────────────────
    /** @type {OctreeNode[]} */
    const parentOctreeNodes = [];
    /** @type {number[]} */
    const parentBvhIds = [];

    /**
     * Recursively convert one octree node into the NaryBvhNodeArray and
     * flat per-node arrays. The bounding box is set for every node.
     * @param {OctreeNode} octreeNode
     * @param {number} bvhId - Pre-assigned BVH node index
     */
    const visitNode = (octreeNode, bvhId) => {
      // Bounding box (set for every node)
      const bounds = octreeNode.boundingBox;
      nodeArray.setBoxThree(bvhId, this._toViewerBox(bounds, this.globalOffset));

      const depth = parentOctreeNodes.length;
      parentOctreeNodes.push(octreeNode);
      parentBvhIds.push(bvhId);
      this._nodeOctreeLevel[bvhId] = depth;

      if (octreeNode.isLeaf) {
        // ── Umbrella leaf ────────────────────────────────────
        const umbrellaIndex = octreeNode.fragmentId;
        const lod0FragId = fragmentIdMap.get(umbrellaIndex * MAX_LOD_LEVELS + 0);
        // Zero-point umbrella leaves still appear as octree leaves, but
        // getRequiredLodLevels skips them so no LOD-0 fragment was allocated.
        if (lod0FragId === undefined) {
          logger.error(`No fragment ID for umbrella ${umbrellaIndex} LOD 0`);
          parentOctreeNodes.pop();
          parentBvhIds.pop();
          return;
        }
        nodeArray.setPrimStart(bvhId, lod0FragId);
        nodeArray.setPrimCount(bvhId, 1);
        this._nodeUmbrellaIndex[bvhId] = umbrellaIndex;
        this._nodeLodLevel[bvhId] = 0;

        // LOD chain children (LOD 1+)
        const leafNode = umbrellaLeafNodes[umbrellaIndex];
        const requiredLods = getRequiredLodLevels(leafNode.numSvoLeafNodes);
        const lodChildIndices = [];
        for (let lod = 1; lod < requiredLods.length; lod++) {
          lodChildIndices.push(nextLodNodeIndex++);
        }
        if (lodChildIndices.length > 0) {
          nodeArray.setChildren(bvhId, [lodChildIndices[0]]);
        }
        nodeArray.setFlags(bvhId, 0);

        for (let i = 0; i < lodChildIndices.length; i++) {
          const lodNodeIndex = lodChildIndices[i];
          const lodLevel = requiredLods[i + 1];
          const fragId = fragmentIdMap.get(umbrellaIndex * MAX_LOD_LEVELS + lodLevel);
          if (fragId === undefined) {
            logger.error(`No fragment ID for umbrella ${umbrellaIndex} LOD ${lodLevel}`);
            continue;
          }
          const viewerBox = this._toViewerBox(bounds, this.globalOffset);
          nodeArray.setBoxThree(lodNodeIndex, viewerBox);
          nodeArray.setPrimStart(lodNodeIndex, fragId);
          nodeArray.setPrimCount(lodNodeIndex, 1);
          this._nodeUmbrellaIndex[lodNodeIndex] = umbrellaIndex;
          this._nodeLodLevel[lodNodeIndex] = lodLevel;

          if (i < lodChildIndices.length - 1) {
            nodeArray.setChildren(lodNodeIndex, [lodChildIndices[i + 1]]);
          }

          /** @type {LodRange} */
          const range = getLodRange(lodLevel);
          let startLevel;
          if (LOD_MAPPING_MODE === 'direct') {
            startLevel = /** @type {number} */range.startLevel;
          } else {
            startLevel = calculateStartLevel(leafNode.numSvoLeafNodes, /** @type {number} */range.startPoint).startLevel;
          }
          // Cell edge length from the AABB: max−min on X only; octree voxels are cubes, so
          // Y and Z spans match and any axis would yield the same value.
          const nodeSize = bounds[3] - bounds[0];
          this._nodeFeatureSize[lodNodeIndex] = Math.pow(Math.pow(0.5, startLevel) * nodeSize, 2);
        }

        // Hierarchical LOD distribution
        this._distributeHlod(octreeNode, bvhId, parentOctreeNodes, parentBvhIds);

      } else {
        // ── Interior node ────────────────────────────────────
        if (octreeNode.children && octreeNode.children.length > 0) {
          const childBvhIds = [];
          for (const childId of octreeNode.children) {
            const childOctreeNode = octreeNodesById.get(childId);
            if (!childOctreeNode) continue;

            const childBvhId = nextNodeId++;
            childBvhIds.push(childBvhId);
            visitNode(childOctreeNode, childBvhId);
          }
          nodeArray.setChildren(bvhId, childBvhIds);
        }
      }

      parentOctreeNodes.pop();
      parentBvhIds.pop();
    };

    // ── Traverse from octree root, merged into the opaque BVH root ──
    if (octreeStructure.nodes.length > 0) {
      visitNode(octreeStructure.nodes[0], opaqueRootId);
    }

    // ── Assign inner-node fragments ──────────────────────────────
    // Nodes with _nodeUmbrellaIndex === -1 and accumulated HLOD points
    // are interior nodes that need a fragment for their pulled-up geometry.
    for (let i = 0; i < baseNodeCount; i++) {
      if (this._nodeUmbrellaIndex[i] === -1 && this._nodePointCount[i] > 0) {
        const fragId = innerNodeFragmentIndex++;
        innerNodeEntries.push({ fragId, bvhNodeId: i });
        nodeArray.setPrimStart(i, fragId);
        nodeArray.setPrimCount(i, 1);
        nodeArray.setFlags(i, 0);
      }
    }

    // ── Primitives array (identity mapping) ──────────────────────
    const numFragments = innerNodeFragmentIndex;
    const primitives = new Int32Array(numFragments);
    for (let i = 0; i < numFragments; i++) primitives[i] = i;

    // ── Initialize fragment data ─────────────────────────────────
    this._initFragmentData(numFragments, umbrellaLeafNodes, innerNodeEntries, fragmentIdMap, nodeArray, options);

    // ── Build NaryBVH ────────────────────────────────────────────
    const bvhOptions = options.bvhOptions ?? {};
    bvhOptions.sortFuncType = BVHSortFuncType.DIAMETER;
    const bvh = new NaryBVH(nodeArray, primitives, bvhOptions);

    // Propagate feature sizes to NaryBvhNodeArray.largestPrimSize: for each BVH node, walk
    // up the parent chain until we find an ancestor with a known `_nodeFeatureSize`, then
    // to make sure inner nodes without own points get a feature size. `tieBreak` applies a
    // tiny relative reduction per hop to make sure the error is strictly decreasing. A relative
    // (multiplicative) factor is used so it stays proportional to the value and never turns negative.
    // TODO: Is this still needed, now that we have a true octree without empty splitting nodes?
    for (let nodeIdx = 0; nodeIdx < bvh.nodes.nodeCount; nodeIdx++) {
      let cur = nodeIdx;
      let tieBreak = 1;
      while (true) {
        if (this._nodeFeatureSize[cur] >= 0) {
          bvh.nodes.setLargestPrimitiveSize(nodeIdx, Math.pow(FEATURE_SIZE_SCALING_FACTOR, 2) * this._nodeFeatureSize[cur] * tieBreak);
          break;
        }
        cur = bvh.getNodeParent(cur);
        tieBreak -= 1e-6;
        if (cur === -1) {
          bvh.nodes.setLargestPrimitiveSize(nodeIdx, Infinity);
          break;
        }
      }
    }

    this.bbox = bbox;

    // Count coarse nodes actually present in the BVH (inner HLOD nodes + LOD-0
    // umbrella leaves). Do not use umbrellaLeafNodes.length — some file entries
    // are not referenced by the umbrella hierarchy and never fire load progress.
    let numCoarseNodes = 0;
    for (let i = 0; i < totalNodeCount; i++) {
      if (this.isCoarseNode(i)) numCoarseNodes++;
    }

    return { bvh, numFragments, numCoarseNodes };
  }

  /**
   * @private
   * @param {RcsHeader|null} header - `null` when the model is empty / placeholder
   * @param {number} numFragments
   * @param {number} numUmbrellaNodes
   * @returns {RcsMetadata}
   */
  _createRcsMetadata(header, numFragments, numUmbrellaNodes) {
    /** @type {RcsMetadata} */
    const metadata = {
      version: header ? `${header.majorVersion}.${header.minorVersion}` : '0.0',
      stats: {
        num_fragments: numFragments,
        num_materials: numFragments > 0 ? 1 : 0,
        num_geoms: numFragments,
        num_umbrella_nodes: numUmbrellaNodes
      },
      "world up vector": { XYZ: [0, 0, 1] }
    };
    return metadata;
  }

  /**
   * Allocate empty Model / `FragmentList` storage for a given fragment count.
   * @private
   * @param {number} numFragments
   * @returns {RcsFragments}
   */
  _createFragmentsShell(numFragments) {
    return {
      length: numFragments,
      fragId2dbId: new Int32Array(numFragments),
      dbId2fragId: new Array(numFragments),
      transforms: new Float32Array(numFragments * 12),
      boxes: new Float32Array(numFragments * 6),
      visibilityFlags: new Uint16Array(numFragments),
      numLoaded: 0,
      initialized: true
    };
  }

  /**
   * Set up minimal model fields when the octree is empty.
   * The bbox is expected to already be in viewer coordinates (globalOffset
   * subtracted by the caller).
   * @private
   * @param {Box3} bbox - Bounding box in viewer coordinates
   * @param {RcsLoadOptions} options
   */
  _initEmptyModel(bbox, options) {
    this.bbox = bbox;
    this.metadata = this._createRcsMetadata(null, 0, 0);
    this.loadOptions = options;
    this.fragments = this._createFragmentsShell(0);
  }

  /**
   * Convert a world-space axis-aligned box to viewer coordinates.
   * @private
   * @param {number[]} bounds - [minX, minY, minZ, maxX, maxY, maxZ]
   * @param {THREE.Vector3} globalOffset
   * @returns {Box3}
   */
  _toViewerBox(bounds, globalOffset) {
    return new Box3(
      new THREE.Vector3(
        bounds[0] - globalOffset.x,
        bounds[1] - globalOffset.y,
        bounds[2] - globalOffset.z
      ),
      new THREE.Vector3(
        bounds[3] - globalOffset.x,
        bounds[4] - globalOffset.y,
        bounds[5] - globalOffset.z
      )
    );
  }

  // ── Private build helpers ────────────────────────────────────────

  /**
   * Distribute points from an umbrella leaf to its ancestor interior nodes
   * for hierarchical LOD. Volume-weighted so each ancestor receives a share
   * proportional to the leaf's contribution to the ancestor's total volume.
   * @private
   * @param {OctreeLeafNode} leafOctreeNode - The umbrella leaf being processed
   * @param {number} leafBvhId - BVH node ID of the leaf
   * @param {OctreeNode[]} parentOctreeNodes - Ancestor stack (including the leaf itself)
   * @param {number[]} parentBvhIds - Parallel BVH-id stack
   */
  _distributeHlod(leafOctreeNode, leafBvhId, parentOctreeNodes, parentBvhIds) {
    const parentLodRanges = [];
    const pointsPerLevel = 100_000;
    let parentIndex = -1;
    let pointsInCurrentParent = 0;
    let maxPointsInParent = 0;
    let summedPoints = 0;
    let maxFeatureSize = 0;

    const descentToNextParent = () => {
      if (parentIndex >= parentOctreeNodes.length - 1) return;
      parentIndex++;
      const ancestorVolume = /** @type {number} */parentOctreeNodes[parentIndex].totalLeafVolume;
      // Degenerate (zero-volume) nodes should not occur, but guard against a
      // division-by-zero that would make maxPointsInParent NaN and collapse all
      // points into the first ancestor. In such a case we conservatively only pull up
      // 1 point, since more doesn't make much sense for a 0 volume node anyways.
      maxPointsInParent = ancestorVolume > 0 ?
      /** @type {number} */leafOctreeNode.totalLeafVolume / ancestorVolume * pointsPerLevel :
      1;
      pointsInCurrentParent = 0;
    };
    descentToNextParent();

    const nodeSize = leafOctreeNode.boundingBox[3] - leafOctreeNode.boundingBox[0];
    const numSvoLeafNodes = leafOctreeNode.numSvoLeafNodes;
    const maxLevelForInnerNodes = LOD_MAPPING_MODE === 'direct' ?
    DIRECT_MODE_LEVELS_IN_LOD0 :
    numSvoLeafNodes.length;

    let lastLevelWithPoints = 0;

    // Walk the coarsest SVO levels and "pull up" their points into ancestor interior
    // nodes so each subtree has a visible approximation.
    // In direct mode this covers exactly the LOD-0 source range (levels 0..DIRECT_MODE_LEVELS_IN_LOD0-1);
    // in threshold mode all levels are eligible but the loop stops once ~1 000 points have been assigned.
    //
    // For each level we start at the shallowest ancestor and accumulate points there until
    // its volume-weighted budget (leafVolume / ancestorVolume × pointsPerLevel) is exceeded,
    // then descend to the next ancestor on the path. The write target and buffer range are
    // recorded in parentLodRanges (keyed on the LOD-0 leaf, one entry per ancestor recipient).
    //
    // Overflow: if every inner ancestor is full, parentIndex ends at the LOD-0 leaf itself
    // and the level's points are assigned there anyway (no further parent to spill into, and
    // no hard cap is enforced at the leaf).
    for (let level = 0; level < maxLevelForInnerNodes && level < numSvoLeafNodes.length; level++) {
      const pointsAtLevel = numSvoLeafNodes[level];
      if (pointsAtLevel === 0) continue;
      lastLevelWithPoints = level;

      while (pointsInCurrentParent + pointsAtLevel > maxPointsInParent) {
        descentToNextParent();
        if (parentIndex >= parentOctreeNodes.length - 1) break;
      }
      pointsInCurrentParent += pointsAtLevel;

      const ancestorBvhId = parentBvhIds[parentIndex];
      // Relative tie-break per hop keeps the error strictly decreasing while staying positive.
      const tieBreak = 1 - (parentOctreeNodes.length - parentIndex) * 1e-6;
      const squaredFeatureSize = Math.pow(Math.pow(0.5, level) * nodeSize, 2);
      const featureSize = squaredFeatureSize * tieBreak;

      this._nodeFeatureSize[ancestorBvhId] = Math.max(this._nodeFeatureSize[ancestorBvhId], featureSize);
      maxFeatureSize = Math.max(maxFeatureSize, squaredFeatureSize);
      this._nodeMaxFeatureSize[ancestorBvhId] = Math.max(
        this._nodeMaxFeatureSize[ancestorBvhId], maxFeatureSize
      );

      const rangeStart = this._nodePointCount[ancestorBvhId];
      const rangeEnd = rangeStart + pointsAtLevel;

      parentLodRanges.push({
        bvhNodeId: ancestorBvhId,
        level,
        rangeStart,
        rangeEnd
      });

      this._nodePointCount[ancestorBvhId] += pointsAtLevel;

      summedPoints += pointsAtLevel;
      if (LOD_MAPPING_MODE === 'threshold' && summedPoints > 1_000) break;
    }

    // Propagate feature sizes to remaining ancestors below the last assigned level
    const leafNodeFeatureSize = Math.pow(Math.pow(0.5, lastLevelWithPoints) * nodeSize, 2);
    maxFeatureSize = Math.max(maxFeatureSize, leafNodeFeatureSize);
    for (parentIndex++; parentIndex < parentOctreeNodes.length; parentIndex++) {
      const tieBreak = 1 - (parentOctreeNodes.length - parentIndex) * 1e-6;
      const ancestorBvhId = parentBvhIds[parentIndex];
      this._nodeFeatureSize[ancestorBvhId] = Math.max(
        this._nodeFeatureSize[ancestorBvhId], leafNodeFeatureSize * tieBreak
      );
      this._nodeMaxFeatureSize[ancestorBvhId] = Math.max(
        this._nodeMaxFeatureSize[ancestorBvhId], maxFeatureSize * tieBreak
      );
    }

    if (parentLodRanges.length > 0) {
      this._nodeParentLodRanges[leafBvhId] = parentLodRanges;
    }
  }

  /**
   * Post-order annotation of octree nodes with cumulative leaf volume.
   * Required before HLOD distribution since the volume-weighted allocation
   * needs `totalLeafVolume` from descendants before visiting leaves.
   * @private
   * @param {RcsOctreeStructure} octreeStructure
   */
  _annotateOctreeWithLeafVolumes(octreeStructure) {
    if (!octreeStructure?.nodes?.length) return;

    const nodes = octreeStructure.nodes;
    const nodeMap = new Map();
    for (const node of nodes) nodeMap.set(node.id, node);

    /** @param {number} nodeId @param {Map<number, OctreeNode>} map @returns {number} */
    function annotate(nodeId, map) {
      const node = map.get(nodeId);
      if (!node) return 0;

      if (node.isLeaf) {
        const bb = node.boundingBox;
        node.totalLeafVolume = (bb[3] - bb[0]) * (bb[4] - bb[1]) * (bb[5] - bb[2]);
      } else {
        node.totalLeafVolume = 0;
        if (node.children) {
          for (const cid of node.children) node.totalLeafVolume += annotate(cid, map);
        }
      }
      return node.totalLeafVolume;
    }

    annotate(nodes[0].id, nodeMap);
  }

  /**
   * Initialize the fragment data arrays (transforms, boxes, visibility, dbId
   * mapping) that satisfy the Model / FragmentList contract.
   * Also sets `this.metadata`, `this.loadOptions`. Expects `this.globalOffset` to already be set.
   *
   * @param {number} numFragments - Total number of fragments
   * @param {UmbrellaLeafNode[]} umbrellaLeafNodes - Umbrella leaf nodes
   * @param {Array<{ fragId: number, bvhNodeId: number }>} innerNodeEntries - Inner-node fragment assignments
   * @param {Map<number, number>} fragmentIdMap - packed (umbrellaIndex, lod) → fragId
   * @param {NaryBvhNodeArray} nodeArray - BVH node array (for reading inner-node bounding boxes)
   * @param {RcsLoadOptions} options - Load options
   * @private
   */
  _initFragmentData(numFragments, umbrellaLeafNodes, innerNodeEntries, fragmentIdMap, nodeArray, options) {
    const header = this.header;
    const numUmbrellaNodes = umbrellaLeafNodes.length;
    // WARNING: debugOctree mode is debugging-only code; production loads must use 'simple'.
    const useDebugOctreeTree = INSTANCE_TREE_MODE === 'debugOctree';

    this.metadata = this._createRcsMetadata(header, numFragments, numUmbrellaNodes);

    this.loadOptions = options;

    const globalOffset = this.globalOffset;

    this.fragments = this._createFragmentsShell(numFragments);

    // Umbrella LOD fragments
    for (let ui = 0; ui < numUmbrellaNodes; ui++) {
      const leafNode = umbrellaLeafNodes[ui];
      const requiredLods = getRequiredLodLevels(leafNode.numSvoLeafNodes);
      for (const lodLevel of requiredLods) {
        const fragId = fragmentIdMap.get(ui * MAX_LOD_LEVELS + lodLevel);
        if (fragId === undefined) continue;

        this.fragments.fragId2dbId[fragId] = useDebugOctreeTree ? fragId : 1;
        const to = fragId * 12;
        this.fragments.transforms[to] = 1;
        this.fragments.transforms[to + 4] = 1;
        this.fragments.transforms[to + 8] = 1;
        const b = leafNode.svoBounds;
        this.fragments.transforms[to + 9] = b[0] - globalOffset.x;
        this.fragments.transforms[to + 10] = b[1] - globalOffset.y;
        this.fragments.transforms[to + 11] = b[2] - globalOffset.z;
        const bo = fragId * 6;
        this.fragments.boxes[bo] = b[0] - globalOffset.x;
        this.fragments.boxes[bo + 1] = b[1] - globalOffset.y;
        this.fragments.boxes[bo + 2] = b[2] - globalOffset.z;
        this.fragments.boxes[bo + 3] = b[3] - globalOffset.x;
        this.fragments.boxes[bo + 4] = b[4] - globalOffset.y;
        this.fragments.boxes[bo + 5] = b[5] - globalOffset.z;
        this.fragments.visibilityFlags[fragId] = MeshFlags.MESH_VISIBLE | MeshFlags.MESH_NOTLOADED | MeshFlags.MESH_HIDE;
      }
    }

    // Inner-node fragments
    const tmpBox = new Float32Array(6);
    for (const { fragId, bvhNodeId } of innerNodeEntries) {
      this.fragments.fragId2dbId[fragId] = useDebugOctreeTree ? fragId : 1;
      nodeArray.getBoxArray(bvhNodeId, tmpBox);
      const to = fragId * 12;
      this.fragments.transforms[to] = 1;
      this.fragments.transforms[to + 4] = 1;
      this.fragments.transforms[to + 8] = 1;
      this.fragments.transforms[to + 9] = tmpBox[0];
      this.fragments.transforms[to + 10] = tmpBox[1];
      this.fragments.transforms[to + 11] = tmpBox[2];
      this.fragments.boxes.set(tmpBox, fragId * 6);
      this.fragments.visibilityFlags[fragId] = MeshFlags.MESH_VISIBLE;
    }

    // Reverse mapping
    for (let fragId = 0; fragId < numFragments; fragId++) {
      const dbId = this.fragments.fragId2dbId[fragId];
      if (!this.fragments.dbId2fragId[dbId]) this.fragments.dbId2fragId[dbId] = [];
      this.fragments.dbId2fragId[dbId].push(fragId);
    }
  }
}